/**
 * @module core/smooth-scroll
 *
 * Optional smooth-scrolling layer built on GSAP's ScrollSmoother.
 *
 * ## Why smoothing is off by default
 *
 * ScrollSmoother drives the page by translating `#smooth-content` inside a
 * fixed wrapper. That transformation changes when `getBoundingClientRect()`
 * reports a position, and `ScrollTrigger` can end up measuring against a scroll
 * position the browser never actually reached. Two symptoms were reproduced
 * here, both of which are unacceptable for this site:
 *
 *  1. **Anchor loads break.** On a `#hash` load the browser sets the scroll
 *     position before the smoother exists; the content is translated to the
 *     anchor but no scroll event follows, so every `ScrollTrigger` stays at
 *     progress 0 and whole sections never reveal. Deep links are a first-class
 *     feature (the search overlay navigates to `#adm`, `#capabilities`, …), so
 *     this alone rules it out for the default path.
 *  2. **Reveals stall.** Sections scrolled past with the smoother active kept
 *     their `onEnter` callbacks unfired, leaving content at `opacity: 0`.
 *
 * Native `scroll-behavior: smooth` (set in `base.css`) gives smooth anchor
 * navigation with exact scroll positions, so nothing is lost by not using it.
 * The smoother stays wired up and can be switched on for experimentation by
 * setting the flag below — before doing so, verify deep links, `ScrollTrigger`
 * reveals and the horizontal journey scrollport in a real browser.
 *
 * Whatever the flag says, components never branch on it: they call
 * `scrollTo()` / `refresh()` on the facade returned by this module.
 */

import {
  gsap,
  ScrollTrigger,
  ScrollSmoother,
  ScrollToPlugin,
  registerPlugins,
} from "./gsap-setup.js";
import { getMotionPreferences } from "./motion.js";
import { debounce } from "./dom.js";
import { reportError } from "./errors.js";

/**
 * Experimental: enable ScrollSmoother-driven inertia scrolling.
 * See the module docblock for the failure modes to re-test before enabling.
 * @type {boolean}
 */
export const ENABLE_SMOOTH_SCROLL = false;

/**
 * @typedef {Object} ScrollController
 * @property {boolean} smooth      Whether smooth scrolling is active.
 * @property {(target: string|Element|number, options?: Object) => void} scrollTo
 * @property {() => void} refresh
 * @property {() => void} destroy
 */

/**
 * Create the scroll controller.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force] Force smooth scrolling on (used in tests).
 * @returns {ScrollController}
 *
 * @example
 * const scroll = createScrollController();
 * scroll.scrollTo("#phase-visibility", { offsetY: 80 });
 */
export function createScrollController(options = {}) {
  registerPlugins();

  const prefs = getMotionPreferences();

  /*
   * Deep links: see the module docblock. Even with ScrollSmoother enabled we
   * skip it on a fragment load, because the anchor jump has already happened.
   */
  const loadedWithFragment = Boolean(window.location.hash && window.location.hash.length > 1);

  const canSmooth =
    options.force ??
    (ENABLE_SMOOTH_SCROLL &&
      !prefs.reducedMotion &&
      prefs.finePointer &&
      window.innerWidth >= 1024 &&
      !loadedWithFragment);

  /** @type {ReturnType<typeof ScrollSmoother.create>|null} */
  let smoother = null;

  if (canSmooth && document.querySelector("#smooth-wrapper") && document.querySelector("#smooth-content")) {
    try {
      smoother = ScrollSmoother.create({
        wrapper: "#smooth-wrapper",
        content: "#smooth-content",
        smooth: 1.15,
        effects: true,
        normalizeScroll: false,
        smoothTouch: false,
      });
    } catch (error) {
      // Smooth scroll is a nicety; failing it must not break the page.
      reportError(error, {
        scope: "core/smooth-scroll",
        userMessage: "Smooth scrolling is unavailable on this device — standard scrolling is active.",
        severity: "warning",
        userFacing: false,
      });
      smoother = null;
    }
  }

  /**
   * Scroll to a target, using ScrollSmoother when present and ScrollToPlugin
   * otherwise, so the behaviour (and easing) stays identical either way.
   *
   * @param {string|Element|number} target
   * @param {{offsetY?: number, duration?: number, immediate?: boolean}} [scrollOptions]
   */
  const scrollTo = (target, scrollOptions = {}) => {
    const { offsetY = 0, duration = 1, immediate = false } = scrollOptions;

    if (smoother) {
      smoother.scrollTo(target, { offsetY, duration: immediate ? 0 : duration });
      return;
    }

    gsap.to(window, {
      duration: immediate ? 0 : duration,
      ease: "power2.inOut",
      scrollTo: { y: target, offsetY, autoKill: true },
    });
  };

  /**
   * Recalculate trigger positions. Debounced because resize fires in bursts and
   * `ScrollTrigger.refresh()` is expensive.
   */
  const refresh = debounce(() => {
    ScrollTrigger.refresh();
  }, 180);

  const onResize = () => refresh();
  const onLoad = () => ScrollTrigger.refresh();

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
  // Web fonts change layout metrics; refresh once they are ready.
  if (document.fonts?.ready) {
    document.fonts.ready.then(onLoad).catch(() => {});
  }
  window.addEventListener("load", onLoad, { once: true });

  const destroy = () => {
    window.removeEventListener("resize", onResize);
    window.removeEventListener("orientationchange", onResize);
    window.removeEventListener("load", onLoad);
    smoother?.kill();
    smoother = null;
  };

  return {
    get smooth() {
      return Boolean(smoother);
    },
    scrollTo,
    refresh: () => ScrollTrigger.refresh(),
    destroy,
  };
}

export { ScrollToPlugin };
